import React from 'react';
import { TrendingDown } from 'lucide-react';

/** Il Prezzo ridotto deciso dal portale (backend `erp/ribassi.py`): il prezzo
 * del listino del Cliente è calato il giorno `dal`, il badge resta fino a
 * `fino` (60 giorni). */
export interface Ribasso {
  dal: string;
  fino: string;
  prezzoPrecedente?: number | null;
}

/**
 * Badge "Prezzo ridotto" (osservazioni del 30/10/2026, #11). Il prezzo
 * precedente NON viene mostrato inline: solo come tooltip al passaggio del
 * mouse (richiesta esplicita utente).
 */
interface RibassoBadgeProps {
  ribasso?: Ribasso | null;
  variant?: 'compact' | 'default';
}

const euro = (n: number) => `€ ${n.toFixed(2).replace('.', ',')}`;
const giorno = (iso: string) => iso.split('-').reverse().join('/');

export function RibassoBadge({ ribasso, variant = 'default' }: RibassoBadgeProps) {
  if (!ribasso) return null;
  const isCompact = variant === 'compact';
  const prima = ribasso.prezzoPrecedente;
  const tooltip = prima && prima > 0
    ? `Prezzo precedente: ${euro(prima)} · ridotto dal ${giorno(ribasso.dal)}`
    : `Prezzo ridotto dal ${giorno(ribasso.dal)}`;

  return (
    <span
      data-testid="ribasso-badge"
      title={tooltip}
      className={`inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-wide bg-red-50 border border-red-200 text-red-700 cursor-help whitespace-nowrap ${
        isCompact ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2 py-0.5'
      }`}
    >
      <TrendingDown className={isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      Prezzo ridotto
    </span>
  );
}

/**
 * Tag "PRO" / "SMART" — mostrato quando l'articolo appartiene a una linea
 * promozionale NTS (campo custom ``ar_hhliton``: "P"=PRO, "S"=SMART).
 */
interface PromoLineBadgeProps {
  lineaPromo?: string | null;
}

export function PromoLineBadge({ lineaPromo }: PromoLineBadgeProps) {
  if (!lineaPromo) return null;
  const isPro = lineaPromo === 'PRO';
  return (
    <span
      data-testid="promo-line-badge"
      title={isPro ? 'Linea promozionale PRO' : 'Linea promozionale SMART'}
      className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md border ${
        isPro
          ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
          : 'bg-teal-50 border-teal-200 text-teal-700'
      }`}
    >
      {lineaPromo}
    </span>
  );
}
